import React, { useEffect, useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useChangeTrackingLens, get, set } from '@totalsoft/change-tracking-react';
import { getErrors, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { useSimpleError, useToast } from 'hooks';
import { UPDATE_INSURANCE_DOCUMENT_COMMENTS } from 'features/policyRenewal/mutations';
import { useConditionalSubscription, useMutation } from 'features/common/security/hooks';
import { useAuthorization } from 'hooks';
import { useSubscription } from '@apollo/client';
import { maxLength } from '@totalsoft/pure-validations';
import { INSURANCE_DOCUMENT_COMMENTS_UPDATED } from 'features/policyRenewal/subscriptions';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { IconButton, TextField } from '@totalsoft/rocket-ui';
import { emptyString } from 'constants/defaultValues';
import { valueOrDefault } from 'utils/functions';
import { isDirty } from '@totalsoft/change-tracking';
import { POLICY_DATA_SAVED } from 'features/insuranceDocument/edit/root/subscriptions';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import { RenewalItemCommentContainer } from '../../PolicyRenewalStyles';

const validator = maxLength(255);

const PolicyRenewalItemCommentsComponent = ({ composeId, documentId, comments }) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToEditRenewal = useAuthorization(AuthorizationCodes.hasRightToEditRenewal, documentTypeId, documentStateId);
  const showError = useSimpleError();
  const addToast = useToast();
  const [t] = useTranslation();

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(comments);
  const [saving, setSaving] = useState(false);

  const [validation, validate] = useDirtyFieldValidation(validator);

  useEffect(() => {
    validate(lens |> get);
  }, [lens, validate]);

  // #region Update

  const [update] = useMutation(UPDATE_INSURANCE_DOCUMENT_COMMENTS, {
    onError: error => {
      showError(error);
      setSaving(false);
    }
  });

  const handleUpdateComments = useCallback(() => {
    if (!validate(lens |> get)) {
      return;
    }
    update({
      variables: {
        input: {
          documentId: documentId,
          comments: lens |> get
        }
      }
    });
    setSaving(true);
  }, [documentId, lens, update, validate]);

  // #endregion

  // #region Subscriptions

  useConditionalSubscription(INSURANCE_DOCUMENT_COMMENTS_UPDATED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({
      data: {
        data: { insuranceDocumentCommentsUpdated }
      }
    }) => {
      addToast(t('Offer.PolicyRenewalList.Actions.UpdatedComments'), 'success', { toastId: 'insuranceDocumentCommentsUpdatedToast' });
      resetLens(insuranceDocumentCommentsUpdated.comments);
      setSaving(false);
    }
  });

  useSubscription(POLICY_DATA_SAVED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({
      data: {
        data: { policyDataSaved }
      }
    }) => {
      resetLens(policyDataSaved?.policyData?.otherInfo);
    }
  });

  // #endregion

  return (
    <Grid container item xs={12} md={6} id={composeId('comments_container')}>
      <strong>{t('Offer.PolicyRenewalList.Field.Comments')}:</strong>
      <RenewalItemCommentContainer>
        <TextField
          id={composeId('comments')}
          fullWidth
          multiline
          onChange={lens |> set}
          disabled={!documentId || !hasRightToEditRenewal}
          value={lens |> get |> valueOrDefault(emptyString)}
          error={!isValid(validation)}
          helperText={getErrors(validation)}
          endAdornment={
            <IconButton
              id={composeId('comments_saveBtn')}
              onClick={handleUpdateComments}
              size={'small'}
              variant='text'
              disabled={!documentId || saving || !isDirty(dirtyInfo)}
            >
              <SaveIcon />
            </IconButton>
          }
        />
      </RenewalItemCommentContainer>
    </Grid>
  );
};

PolicyRenewalItemCommentsComponent.propTypes = {
  composeId: PropTypes.func.isRequired,
  documentId: PropTypes.number,
  comments: PropTypes.string
};

export default PolicyRenewalItemCommentsComponent;
